// services/platform.ts
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { hapticService } from './haptics';
import { detectWebAppType, rememberNativeAppType, resolveKnownAppType } from '../utils/webAppType';
import { BUILT_PRODUCT, getProduct } from '@/product';
import { applyStatusBar, applyTelegramChrome } from '@/theme/theme';
import type { TelegramWebApp, AppType } from '../types';
import { Platform, PlatformCapabilities, HapticFeedbackOptions } from '../types/platform';

class PlatformService {
    private _platform: Platform | null = null;
    private _capabilities: PlatformCapabilities | null = null;
    private _appType: AppType | null = null;

    /**
     * Detect the current platform
     */
    detectPlatform(): Platform {
        if (this._platform) {
            return this._platform;
        }

        // Check for Telegram Mini App first (highest priority)
        if (this.isTelegramMiniApp()) {
            this._platform = Platform.TELEGRAM;
            return this._platform;
        }

        // Check for Capacitor native app
        if (Capacitor.isNativePlatform()) {
            const platform = Capacitor.getPlatform();
            if (platform === 'ios') {
                this._platform = Platform.IOS;
            } else if (platform === 'android') {
                this._platform = Platform.ANDROID;
            } else {
                this._platform = Platform.WEB;
            }
            return this._platform;
        }

        // Default to web
        this._platform = Platform.WEB;
        return this._platform;
    }

    /**
     * Check if running as Telegram Mini App
     */
    isTelegramMiniApp(): boolean {
        return !!(window.Telegram?.WebApp?.initData);
    }

    /**
     * Check if running as native app (iOS/Android)
     */
    isNativeApp(): boolean {
        return Capacitor.isNativePlatform();
    }

    /**
     * Alias for isNativeApp
     */
    isNative(): boolean {
        return this.isNativeApp();
    }

    /**
     * Check if running on iOS
     */
    isIOS(): boolean {
        return this.detectPlatform() === Platform.IOS;
    }

    /**
     * Check if running on Android
     */
    isAndroid(): boolean {
        return this.detectPlatform() === Platform.ANDROID;
    }

    /**
     * Check if running on web
     */
    isWeb(): boolean {
        return this.detectPlatform() === Platform.WEB;
    }

    /**
     * Get app type (greekbrain or hallouminati)
     * For native apps: determined by bundle ID
     * For web/Telegram: determined by hostname
     */
    async getAppType(): Promise<AppType> {
        if (this._appType) {
            return this._appType;
        }

        // A product-pinned bundle never has to ask the native shell.
        if (BUILT_PRODUCT) {
            this._appType = BUILT_PRODUCT;
            return this._appType;
        }

        try {
            if (this.isNativeApp()) {
                // Get bundle ID from native app using built-in App plugin
                const info = await App.getInfo();

                if (info.id.includes('hallouminati')) {
                    this._appType = 'hallouminati';
                } else {
                    this._appType = 'greekbrain';
                }

                // Cache it so the next launch can pick the Sentry project (and
                // anything else that is per-app) before this await resolves.
                rememberNativeAppType(this._appType);
            } else {
                // For web/Telegram: hostname first (GreekBrain has its own
                // domain), then pathname for the shared app.hallouminati.app host.
                this._appType = detectWebAppType();
            }
        } catch (error) {
            console.error('Failed to get app type:', error);
            this._appType = 'hallouminati'; // fallback
        }

        return this._appType;
    }

    /**
     * The app type without awaiting anything, for callers that run inside a
     * synchronous path (request interceptors). Null only on a fresh native
     * install before `getAppType()` has resolved the bundle id once.
     */
    getResolvedAppType(): AppType | null {
        return this._appType ?? resolveKnownAppType();
    }

    /**
     * Get app name
     */
    async getAppName(): Promise<string> {
        try {
            if (this.isNativeApp()) {
                const info = await App.getInfo();
                return info.name;
            } else {
                const appType = await this.getAppType();
                return getProduct(appType).name;
            }
        } catch (error) {
            console.error('Failed to get app name:', error);
            return 'GreekBrain';
        }
    }

    /**
     * Get platform capabilities
     */
    getCapabilities(): PlatformCapabilities {
        if (this._capabilities) {
            return this._capabilities;
        }

        const platform = this.detectPlatform();
        console.log(platform);
        this._capabilities = {
            hasHapticFeedback: hapticService.isSupported(),
            hasPushNotifications: platform === Platform.TELEGRAM || this.isNativeApp(),
            hasNativeAuth: this.isNativeApp(),
            supportsAppleSignIn: platform === Platform.IOS || platform === Platform.WEB,
            isNativeApp: this.isNativeApp(),
            isTelegramMiniApp: this.isTelegramMiniApp(),
        };

        return this._capabilities;
    }

    /**
     * Trigger haptic feedback.
     *
     * @deprecated Prefer the dedicated `hapticService` (services/haptics.ts) or
     * `usePlatform().hapticFeedback`. Kept as a thin delegate for compatibility.
     */
    async hapticFeedback(options: HapticFeedbackOptions = {}): Promise<void> {
        hapticService.trigger(options);
    }

    /**
     * Get OAuth redirect URL based on platform
     */
    getOAuthRedirectUrl(provider: 'google' | 'apple'): string {
        if (this.isNativeApp()) {
            // Use custom URL scheme for native apps
            // Get the scheme from the current app type
            const scheme = getProduct(this._appType ?? 'greekbrain').urlScheme;
            return `${scheme}://oauth/${provider}/callback`;
        }

        // Web or Telegram: use API URL for OAuth callback
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        return `${apiBaseUrl}/auth/${provider}/callback`;
    }

    /**
     * Get Telegram WebApp instance if available
     */
    getTelegramWebApp(): TelegramWebApp | null {
        if (this.isTelegramMiniApp()) {
            return window.Telegram?.WebApp || null;
        }
        return null;
    }

    /**
     * Initialize platform-specific features
     */
    async initialize(): Promise<void> {
        const platform = this.detectPlatform();
        console.log('Platform initialized:', platform);

        // Get app type
        const appType = await this.getAppType();
        console.log('App type:', appType);

        if (platform === Platform.TELEGRAM) {
            const tg = window.Telegram?.WebApp;
            if (tg) {
                tg.ready();
                tg.expand();

                // Header and background follow the theme (theme/theme.ts); this
                // first paint uses whatever the boot script in index.html chose.
                applyTelegramChrome();

                // Request fullscreen on mobile Telegram clients
                const isMobile = ['android', 'android_x', 'ios'].includes(tg.platform);
                if (isMobile) {
                    // Read safeAreaInset + contentSafeAreaInset from the JS API (reliable on
                    // both iOS and Android) and write the sum as a single CSS variable.
                    // env(safe-area-inset-top) only works on iOS; the Telegram-injected
                    // --tg-safe-area-inset-top CSS variable may arrive too late or be 0.
                    const updateTopInset = () => {
                        const sys = tg.safeAreaInset?.top ?? 0;
                        const content = tg.contentSafeAreaInset?.top ?? 0;
                        document.documentElement.style.setProperty(
                            '--tg-app-top-inset',
                            `${sys + content}px`
                        );
                        // The status-bar strip alone, above Telegram's own buttons:
                        // where SafeAreaBrand puts the app's name for screenshots.
                        document.documentElement.style.setProperty(
                            '--tg-device-top-inset',
                            `${tg.isFullscreen ? sys : 0}px`
                        );
                    };
                    tg.onEvent('safeAreaChanged', updateTopInset);
                    tg.onEvent('contentSafeAreaChanged', updateTopInset);

                    tg.disableVerticalSwipes();
                    tg.requestFullscreen();
                    tg.onEvent('fullscreenChanged', () => {
                        document.documentElement.classList.toggle('tg-fullscreen', tg.isFullscreen);
                        updateTopInset();
                    });
                    if (tg.isFullscreen) {
                        document.documentElement.classList.add('tg-fullscreen');
                    }
                    updateTopInset();
                }
            }
        } else if (this.isNativeApp()) {
            // Status bar text colour follows the theme (theme/theme.ts).
            await applyStatusBar();

            // Setup push notification listeners (do not register yet)
            // await this.setupPushListeners(); // Moved to notificationService
        }
    }
    /**
     * Get app version
     */
    async getAppVersion(): Promise<string> {
        try {
            const info = await App.getInfo();
            return info.version;
        } catch (error) {
            console.warn('Failed to get app version:', error);
            return '1.0.0';
        }
    }

    /**
     * Get device ID
     */
    async getDeviceId(): Promise<string> {
        try {
            const info = await Device.getId();
            return info.identifier;
        } catch (error) {
            console.warn('Failed to get device ID:', error);
            return 'unknown';
        }
    }

    /**
     * Get device model
     */
    async getDeviceModel(): Promise<string> {
        try {
            const info = await Device.getInfo();
            return info.model;
        } catch (error) {
            console.warn('Failed to get device model:', error);
            return 'unknown';
        }
    }

    /**
     * Get APNs environment (production vs sandbox)
     * Returns 'sandbox' for development builds and 'production' for release builds
     */
    async getAPNsEnvironment(): Promise<'production' | 'sandbox'> {
        if (!this.isIOS()) {
            return 'production'; // Only relevant for iOS
        }

        try {
            // Check if running in development mode
            // Development builds use APNs sandbox, production builds use production
            return import.meta.env.VITE_APNS_ENVIRONMENT ?? 'sandbox';
        } catch (error) {
            console.warn('Failed to detect APNs environment:', error);
            return 'production'; // Safe default
        }
    }
}

export const platformService = new PlatformService();